/* ===========================================================
   pages/DocPage.jsx — one open doc

   Opens from this device first (instant, works offline), then joins
   the doc's live room. Top bar: back, title, save status, people
   here, Share, Version history, menu. Panels slide in on the right
   (full screen on phones).
   =========================================================== */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { session, mode, store, online, docContext, pref, docs } from '../state.js';
import { go, beforeLeave } from '../lib/router.js';
import { api } from '../lib/api.js';
import { DocSync } from '../sync/provider.js';
import { MemoryStore } from '../sync/memstore.js';
import { connector } from '../sync/connect.js';
import { findDoc, noteContent, refreshList, trashDoc, createDoc, starDoc } from '../sync/docs.js';
import { saveVersion } from '../sync/versions.js';
import { toJSON, applyJSON, summary, titleOf } from '../editor/convert.js';
import { EditorView } from '../editor/EditorView.jsx';
import { Toolbar, askLink } from '../editor/Toolbar.jsx';
import { equationDialog } from '../editor/EquationDialog.jsx';
import { env } from '../editor/env.js';
import { Icon, IconButton, Button, Menu, Spinner, Avatar, toast, colourFor, confirmDialog, promptDialog } from '../ui/ui.jsx';
import { ProtoCard, Proto, offlineLine } from '../ui/proto.jsx';
import { SharePanel } from '../panels/Share.jsx';
import { HistoryPanel, makeEditCopy } from '../panels/History.jsx';
import { ConflictBanner } from '../panels/Conflicts.jsx';
import { downloadDoc } from '../files/export.js';
import { TabStrip, Outline, PreviewPane, useTabs } from '../panels/DocSide.jsx';
import { fieldOf, tabsOf, addTab } from '../editor/tabs.js';
import { TasksTab } from '../panels/TasksTab.jsx';
import { w } from '../lib/words.js';

const STATUS = {
  loading: ['Opening…', 'cloud'],
  connecting: ['Saved on this device', 'check'],
  saving: ['Saving…', 'cloud'],
  saved: ['Saved', 'check'],
  offline: ['Offline', 'cloudOff'],
  'offline-pending': ['Offline — changes will sync', 'cloudOff'],
  blocked: ['Not syncing — saved on this device', 'cloudOff'],
  gone: ['Not available', 'cloudOff'],
};

export function StatusPill({ status }) {
  const [text, icon] = STATUS[status?.state] || STATUS.loading;
  const title = status?.blocked?.msg || (status?.state === 'connecting' ? 'Connecting to sync…' : text);
  return (
    <span class={`status s-${status?.state || 'loading'}`} title={title} role="status" aria-live="polite">
      <Icon name={icon} size={15} /><span class="status-text">{text}</span>
    </span>
  );
}

export function DocPage({ id, link }) {
  const s = session.value;
  const signedIn = !!s.user && s.status === 'ready';
  const [meta, setMeta] = useState(() => findDoc(id) || null);
  const [sync, setSync] = useState(null);
  const [status, setStatus] = useState({ state: 'loading' });
  const [editor, setEditor] = useState(null);
  const [role, setRole] = useState(meta?.role || (link ? 'view' : null));
  const [error, setError] = useState(null);
  const [panel, setPanel] = useState(null);           // 'share' | 'history'
  const [peers, setPeers] = useState([]);
  const [title, setTitle] = useState('');
  const [conflicts, setConflicts] = useState(null);
  const [sourceOn, setSourceOn] = useState(false);
  const [Source, setSource] = useState(null);
  // tabs, outline, side preview (2026-10-28)
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'default');
  const [preview, setPreview] = useState(null);       // { key, code } of a live HTML/CSS/JS block
  const [outlineOn, setOutlineOn] = useState(() => {
    try { const v = localStorage.getItem('dp:outline'); return v ? v === '1' : window.innerWidth >= 1100; } catch { return true; }
  });
  const tabs = useTabs(sync);
  const lastVersion = useRef(0);
  const editedSince = useRef(false);

  /* ---------- open the doc ---------- */
  useEffect(() => {
    let alive = true;
    let ds = null;
    setError(null);
    setSync(null);
    setStatus({ state: 'loading' });
    (async () => {
      let m = findDoc(id);
      if (!m && online.value) {
        try {
          if (link) {
            const r = await api(`/api/share/${encodeURIComponent(link)}`, { method: 'POST' });
            m = r.doc;
            if (signedIn) refreshList();
          } else {
            m = (await api(`/api/docs/${id}`)).doc;
          }
        } catch (err) {
          if (alive) setError(err);
          return;
        }
      }
      if (!m && !signedIn) { setError({ status: 404 }); return; }
      if (!m) { setError({ offlineMissing: true }); return; }
      if (!alive) return;
      setMeta(m);
      setRole(m.role);
      const st = signedIn ? store.value : new MemoryStore();
      ds = new DocSync({
        docId: id, store: st, dev: localStorage.getItem('dp:dev') || 'dev', role: m.role,
        connect: connector(id, { link, signedIn }), toJSON, applyJSON,
      });
      ds.on('status', st2 => alive && setStatus(st2));
      ds.on('role', r => alive && setRole(r));
      ds.on('fatal', e => alive && setError({ fatal: e }));
      ds.on('conflicts', c => { if (alive) { setConflicts(c); toast('You and someone else changed the same spot while you were offline. I kept both; pick which to keep.', { kind: 'info', proto: true, ms: 9000 }); } });
      ds.on('blocked', b => toast(b.msg, { kind: 'error' }));
      ds.on('peers', () => alive && setPeers(otherPeople(ds)));
      ds.awareness.on('change', () => alive && setPeers(otherPeople(ds)));
      await ds.start();
      if (!alive) { ds.destroy(); return; }
      setSync(ds);
      setTitle(titleOf(ds.doc) || m.title || '');
      const c = await st.getConflicts(id);
      if (c && alive) setConflicts(c);
    })();
    return () => { alive = false; if (ds) { ds.destroy(); } };
  }, [id, link, signedIn]);

  /* ---------- online / offline / leaving ---------- */
  useEffect(() => {
    if (!sync) return;
    const on = () => sync.online();
    const off = () => { sync.offline(); toast(offlineLine(), { proto: true }); };
    // hidden: save to this device; back after a while away: reconnect now, don't wait out the backoff
    const hide = () => { if (document.visibilityState === 'hidden') sync.flushLocal(); else sync.online(); };
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', hide);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      document.removeEventListener('visibilitychange', hide);
      window.removeEventListener('pagehide', hide);
    };
  }, [sync]);

  const canEdit = role === 'owner' || role === 'edit';
  const isGuest = !signedIn;

  /* ---------- title (kept inside the doc, so it syncs and works offline) ---------- */
  useEffect(() => {
    if (!sync) return;
    const metaMap = sync.doc.getMap('meta');
    const obs = () => setTitle(String(metaMap.get('title') || ''));
    metaMap.observe(obs);
    return () => metaMap.unobserve(obs);
  }, [sync]);

  const setDocTitle = v => {
    setTitle(v);
    sync?.doc.getMap('meta').set('title', v.slice(0, 200));
  };

  /* ---------- keep the list, search and server in step (debounced) ---------- */
  useEffect(() => {
    if (!sync || isGuest) return;
    let timer = null;
    const onUpdate = (_u, origin) => {
      if (origin !== 'dp:load') editedSince.current = editedSince.current || origin !== 'dp:remote';
      clearTimeout(timer);
      timer = setTimeout(() => {
        const json = toJSON(sync.doc);
        noteContent(id, { title: titleOf(sync.doc), ...summary(json) }, canEdit);
      }, 1500);
    };
    sync.doc.on('update', onUpdate);
    return () => { clearTimeout(timer); sync.doc.off('update', onUpdate); };
  }, [sync, canEdit]);

  /* ---------- tabs: the open one has to exist (someone may delete it) ---------- */
  // read the doc itself, not the tab state (that can still be empty the moment the doc opens)
  useEffect(() => { if (sync && !tabsOf(sync.doc).some(t => t.id === tab)) setTab('default'); }, [tabs, sync]);
  useEffect(() => { setTab(new URLSearchParams(location.search).get('tab') || 'default'); setPreview(null); }, [id]);
  // his Updates doc gets a "Tell Claude" tab (owner only); wait a moment so a doc still loading isn't given a second one
  const isUpdates = s.owner && role === 'owner' && (pref('updatesDoc', null) === id || /^protocol updates$/i.test((title || '').trim()));
  useEffect(() => {
    if (!sync || !isUpdates) return;
    const t = setTimeout(() => { if (!tabsOf(sync.doc).some(x => x.kind === 'tasks')) addTab(sync.doc, 'Tell Claude', { kind: 'tasks' }); }, 2500);
    return () => clearTimeout(t);
  }, [sync, isUpdates]);
  const tabKind = tabs.find(t => t.id === tab)?.kind;
  const pickTab = t => {
    setTab(t);
    const u = new URL(location.href);
    if (t === 'default') u.searchParams.delete('tab'); else u.searchParams.set('tab', t);
    history.replaceState(history.state, '', u);
  };
  const showOutline = on => { setOutlineOn(on); try { localStorage.setItem('dp:outline', on ? '1' : '0'); } catch { /* fine */ } };

  /* ---------- the toolbar stays pinned right under the header, whatever its real height ---------- */
  useEffect(() => {
    const top = document.querySelector('.doc-top');
    if (!top) return;
    const set = () => document.documentElement.style.setProperty('--top-h', `${Math.ceil(top.getBoundingClientRect().height)}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(top);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty('--top-h'); };
  }, [sync]);

  /* ---------- a live preview block asked for the side pane ---------- */
  useEffect(() => {
    const open = e => setPreview(e.detail);
    const code = e => setPreview(p => (p && p.key === e.detail.key ? e.detail : p));
    window.addEventListener('dp:preview-open', open);
    window.addEventListener('dp:preview-code', code);
    return () => { window.removeEventListener('dp:preview-open', open); window.removeEventListener('dp:preview-code', code); };
  }, []);

  /* ---------- automatic versions: every 10 minutes of editing, and on leaving ---------- */
  useEffect(() => {
    if (!sync || !canEdit || isGuest) return;
    const auto = async () => {
      if (!editedSince.current || !sync.synced || sync.unsynced()) return;
      if (Date.now() - lastVersion.current < 9 * 60_000) return;
      editedSince.current = false;
      lastVersion.current = Date.now();
      try { await saveVersion(id, sync.doc, { kind: 'auto' }); } catch { editedSince.current = true; }
    };
    lastVersion.current = Date.now() - 8 * 60_000;   // first one ~1 min after editing starts
    const t = setInterval(auto, 60_000);
    const unguard = beforeLeave(() => { if (editedSince.current && sync.synced) { lastVersion.current = 0; auto(); } return true; });
    return () => { clearInterval(t); unguard(); };
  }, [sync, canEdit]);

  /* ---------- editor environment (equations, previews, runner) ---------- */
  useEffect(() => {
    env.mode = () => mode.value;
    env.autoRunPreviews = () => role === 'owner' || (meta?.ownerId && meta.ownerId === session.value.user?.id);
    env.editEquation = opts => equationDialog(opts);
    env.notify = msg => toast(msg);
    env.timeout = () => Number(pref('timeout', 10)) || 10;
  }, [role, meta]);

  /* ---------- search menu actions ---------- */
  useEffect(() => {
    docContext.value = { id, editor, sync, canEdit, role, openPanel: setPanel, title, toggleSource: () => setSourceOn(v => !v) };
    // test hook for local runs only (never on the real site)
    if (location.hostname === '127.0.0.1' || location.hostname === 'localhost') window.__dp = { id, editor, sync, toJSON, applyJSON };
    return () => { docContext.value = null; };
  }, [id, editor, sync, canEdit, role, title]);

  useEffect(() => {
    if (!sourceOn || Source) return;
    import('../dev/dev-source.jsx').then(m => setSource(() => m.SourceView)).catch(err => { toast(err, { kind: 'error' }); setSourceOn(false); });
  }, [sourceOn]);

  /* ---------- render ---------- */
  if (error) return <DocError error={error} signedIn={signedIn} />;

  const people = peers;
  const user = { name: s.profile?.name || 'Guest', color: colourFor(s.user?.id || 'guest') };
  const parent = meta?.kind === 'copy' ? findDoc(meta.parentId) : null;
  const font = pref('font', 'sans');
  const width = pref('width', 'normal');

  const menuItems = [
    signedIn && { label: 'Make a copy', icon: 'copy', onSelect: () => duplicate(sync, title) },
    signedIn && canEdit && { label: w('tryCopy'), icon: 'branch', onSelect: () => makeEditCopy({ id, sync, title }) },
    { label: w('history'), icon: 'history', onSelect: () => setPanel('history') },
    'sep',
    { label: 'Download as Markdown (.md)', icon: 'download', onSelect: () => downloadDoc(sync, title, 'md') },
    { label: 'Download as text (.txt)', icon: 'download', onSelect: () => downloadDoc(sync, title, 'txt') },
    { label: 'Download as web page (.html)', icon: 'download', onSelect: () => downloadDoc(sync, title, 'html') },
    { label: 'Print or save as PDF', icon: 'print', onSelect: () => window.print() },
    'sep',
    signedIn && { label: meta?.starred ? 'Remove star' : 'Star', icon: 'star', onSelect: () => { starDoc(id, !meta?.starred); setMeta({ ...meta, starred: !meta?.starred }); } },
    s.owner && canEdit && { label: 'Proofread (beta)', icon: 'sparkle', onSelect: () => import('../editor/autocorrect.js').then(m => { const n = m.proofread(editor); toast(n ? `Proofread: fixed ${n} thing${n === 1 ? '' : 's'}. Ctrl+Z undoes it.` : 'Proofread: nothing to fix.'); }) },
    { label: 'Word count', icon: 'info', onSelect: () => toast(`${editor?.storage.characterCount.words() ?? 0} words · ${editor?.storage.characterCount.characters() ?? 0} characters`) },
    signedIn && 'sep',
    signedIn && { label: role === 'owner' ? 'Move to trash' : 'Remove from my docs', icon: 'trash', danger: true, onSelect: async () => {
      if (!await confirmDialog({ title: role === 'owner' ? 'Move to trash?' : 'Remove from your docs?', text: role === 'owner' ? 'You can restore it from the trash for 30 days.' : 'The owner still keeps it. You can open the share link again to get it back.', ok: role === 'owner' ? 'Move to trash' : 'Remove', danger: true })) return;
      await trashDoc(id);
      go('/');
    } },
  ];

  return (
    <div class={`docpage width-${width} ${panel ? 'with-panel' : ''} ${preview ? 'with-preview' : ''} ${outlineOn && sync ? 'with-outline' : ''}`}>
      <header class="doc-top">
        <a href={signedIn ? '/' : '/'} class="doc-back" aria-label={signedIn ? 'Back to your docs' : 'Docs Protocol home'} title="Your docs">
          <Logo />
        </a>
        <div class="doc-titlebox">
          <input class="doc-title" value={title} placeholder="Untitled document" aria-label="Document title" maxLength={200}
            readOnly={!canEdit} onInput={e => setDocTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); editor?.commands.focus('start'); } }} />
          <div class="doc-sub">
            <StatusPill status={status} />
            {role === 'view' && <span class="tag">View only</span>}
            {meta?.kind === 'copy' && <span class="tag accent">{w('copy')}</span>}
          </div>
        </div>
        <div class="doc-actions">
          <div class="people" aria-label={`${people.length} other ${people.length === 1 ? 'person' : 'people'} here`}>
            {people.slice(0, 4).map(p => <Avatar key={p.id} name={p.name} seed={p.color} size={28} />)}
          </div>
          <IconButton icon="search" label="Search (Ctrl K)" class="hide-sm" onClick={() => window.dispatchEvent(new CustomEvent('dp:palette'))} />
          <IconButton icon="history" label={w('history')} active={panel === 'history'} onClick={() => setPanel(p => (p === 'history' ? null : 'history'))} />
          {role === 'owner' && <Button kind="primary" icon="share" class="share-btn" onClick={() => setPanel(p => (p === 'share' ? null : 'share'))}>Share</Button>}
          <Menu align="right" label="Doc menu" trigger={<IconButton icon="more" label="More" />} items={menuItems} />
        </div>
      </header>

      {canEdit && <Toolbar editor={editor} mode={mode.value} docId={id} sourceOn={sourceOn} onToggleSource={() => setSourceOn(v => !v)} />}
      {sync && (
        <div class="doctabs-row">
          {!outlineOn && <IconButton icon="menu" label="Show the outline" onClick={() => showOutline(true)} />}
          <TabStrip sync={sync} tabs={tabs} active={tab} onPick={pickTab} canEdit={canEdit} />
        </div>
      )}

      {isGuest && (
        <div class="banner">
          <Icon name="eye" size={16} /><span>You're viewing a shared doc. <a href="/">Sign in</a> to edit docs shared with you, or make your own.</span>
        </div>
      )}
      {parent !== null && meta?.kind === 'copy' && (
        <div class="banner accent">
          <Icon name="branch" size={16} />
          <span>This is an {w('copy')} of <a href={`/d/${meta.parentId}`}>{parent?.title || 'the original'}</a>. Try anything here; the original stays as it was.</span>
          <span class="grow" />
          <Button size="sm" kind="primary" icon="merge" onClick={() => setPanel('history')}>{w('mergeBack')}</Button>
        </div>
      )}
      {conflicts && sync && <ConflictBanner sync={sync} rec={conflicts} onDone={() => setConflicts(null)} />}

      <div class="doc-body">
        {sync && outlineOn && <Outline editor={editor} tabs={tabs} active={tab} onPick={pickTab} docId={id} onClose={() => showOutline(false)} />}
        <main class="doc-scroll" id="main">
          {!sync ? (
            <div class="page loading-page"><Spinner label="Opening the doc" /></div>
          ) : (
            <div class="page">
              {tabKind === 'tasks' ? (
                isUpdates || s.owner ? <TasksTab /> : <p class="hint">This tab is only for the doc's owner.</p>
              ) : sourceOn && Source ? (
                <Source sync={sync} field={fieldOf(tab)} onClose={() => setSourceOn(false)} />
              ) : (
                <EditorView key={`${mode.value}-${role}-${tab}`} sync={sync} editable={canEdit && tabKind !== 'claude'} user={user} docId={id} mode={mode.value}
                  font={font} spellcheck={pref('spellcheck', true)} onEditor={setEditor} field={fieldOf(tab)} />
              )}
            </div>
          )}
        </main>
        {preview && <PreviewPane code={preview.code} onClose={() => setPreview(null)} />}
        {panel === 'share' && <SharePanel id={id} title={title} onClose={() => setPanel(null)} />}
        {panel === 'history' && sync && <HistoryPanel id={id} sync={sync} meta={meta} title={title} canEdit={canEdit} onClose={() => setPanel(null)} />}
      </div>
    </div>
  );
}

function otherPeople(ds) {
  const out = [];
  ds.awareness.getStates().forEach((st, clientId) => {
    if (clientId === ds.doc.clientID || !st?.user) return;
    out.push({ id: clientId, name: st.user.name, color: st.user.color });
  });
  return out;
}

async function duplicate(sync, title) {
  const name = await promptDialog({ title: 'Make a copy', label: 'Name of the copy', value: `Copy of ${title || 'Untitled document'}`, ok: 'Make copy' });
  if (name === null) return;
  const id = await createDoc({ title: name, json: toJSON(sync.doc) });
  go(`/d/${id}`);
  toast('Copy made. It\'s a separate doc now.');
}

function DocError({ error, signedIn }) {
  const back = <Button kind="primary" onClick={() => go('/')}>{signedIn ? 'Back to your docs' : 'Go to Docs Protocol'}</Button>;
  if (error.offlineMissing) {
    return <div class="center-page"><ProtoCard title="Not on this device yet" text="This doc hasn't been opened on this device before, and we're offline, so I can't fetch it. It'll open as soon as the internet is back." actions={back} /></div>;
  }
  if (error.fatal?.code === 'signedout') {
    // the sign-in screen keeps this address, so signing in lands right back in the doc
    return <div class="center-page"><ProtoCard title="You were signed out" text={error.fatal.msg} actions={<Button kind="primary" onClick={() => { session.value = { status: 'signed-out' }; }}>Sign in</Button>} /></div>;
  }
  if (error.fatal) {
    return <div class="center-page"><ProtoCard title={error.fatal.code === 'deleted' ? 'This doc was deleted' : 'No access'} text={error.fatal.msg} actions={back} /></div>;
  }
  if (error.status === 401 || (error.status === 404 && !signedIn)) {
    return <div class="center-page"><ProtoCard title="Sign in to open this" text="This doc is private, or the link was turned off. If it was shared with you, sign in with the account it was shared to." actions={back} /></div>;
  }
  return <div class="center-page"><ProtoCard title="Couldn't open this doc" error={error} actions={back} /></div>;
}

export function Logo({ size = 28 }) {
  return (
    <span class="logo" aria-hidden="true">
      <svg viewBox="0 0 32 32" width={size} height={size}>
        <rect x="5" y="3" width="22" height="26" rx="4" fill="var(--a)" />
        <path d="M10 11h12M10 16h12M10 21h8" stroke="var(--on-a)" stroke-width="2.4" stroke-linecap="round" />
      </svg>
    </span>
  );
}
